import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getCardClass, isCardTransparent, getAnimation, hexToRgba } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

const DAY_NAMES = { 'Lunes': 1, 'Martes': 2, 'Miercoles': 3, 'Jueves': 4, 'Viernes': 5, 'Sabado': 6, 'Domingo': 0 };

export default function HoursSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const items = content.items || [];
    const anim = getAnimation(config, visible);
    const card = getCardClass(cardStyle, s.dark);
    const tp = isCardTransparent(cardStyle, s.dark);
    const variant = config.variant || 'card';
    const highlightToday = content.highlight_today !== false;

    if (items.length === 0) return null;

    const today = new Date().getDay();
    const isToday = (dayName) => highlightToday && DAY_NAMES[dayName] === today;

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    // ── Inline variant ──
    if (variant === 'inline') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title || 'Horario'} subtitle={content.subtitle} config={config} s={s} />
                    <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
                        {items.map((item, i) => (
                            <div key={i} className={`px-4 py-3 rounded-xl text-center transition-all duration-300 ${isToday(item.day) ? 'ring-2 scale-105' : ''} ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}
                                style={{
                                    backgroundColor: isToday(item.day) ? hexToRgba(s.accent, 0.08) : (s.dark ? 'rgba(255,255,255,0.05)' : '#f9fafb'),
                                    ringColor: isToday(item.day) ? s.accent : undefined,
                                    transitionDelay: visible ? `${i * 60}ms` : '0ms',
                                }}>
                                <div className="text-xs font-bold uppercase tracking-wider"
                                    style={{ color: isToday(item.day) ? s.accent : s.textSub }}>{item.day}</div>
                                <div className="text-sm font-semibold mt-1"
                                    style={{ color: item.closed ? '#f87171' : s.text }}>
                                    {item.closed ? (content.closed_label || 'Cerrado') : `${item.open} - ${item.close}`}
                                </div>
                                {isToday(item.day) && <span className="text-[9px] font-bold uppercase tracking-wider mt-1 block" style={{ color: s.accent }}>{content.today_label || 'Hoy'}</span>}
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Card (default) ──
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title || 'Horario'} subtitle={content.subtitle} config={config} s={s} />
                <div className={`${card} max-w-md mx-auto p-6 transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                    <div className="space-y-0">
                        {items.map((item, i) => (
                            <div key={i} className={`flex items-center justify-between py-3 transition-all duration-300 ${i > 0 ? 'border-t' : ''} ${isToday(item.day) ? 'px-3 -mx-3 rounded-lg' : ''}`}
                                style={{ ...(i > 0 ? { borderColor: s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6' } : {}), ...(isToday(item.day) ? { backgroundColor: hexToRgba(s.accent, 0.06) } : {}) }}>
                                <div className="flex items-center gap-3">
                                    {isToday(item.day) && <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: s.accent }} />}
                                    <span className="text-sm font-semibold"
                                        style={{ color: isToday(item.day) ? s.accent : s.text }}>
                                        {item.day}
                                        {isToday(item.day) && <span className="text-[9px] ml-1.5 opacity-70">({content.today_label || 'hoy'})</span>}
                                    </span>
                                </div>
                                <span className={`text-sm ${item.closed ? 'font-medium' : ''}`} style={{ color: item.closed ? '#f87171' : s.textSub }}>
                                    {item.closed ? (content.closed_label || 'Cerrado') : `${item.open} - ${item.close}`}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
